import { EntityType } from "@screencloud/signage-firestore-client";
import { useCallback, useEffect, useState } from "react";
import { CompareChangesModal } from "src/components/CompareChangesModal/CompareChangesModal";
import { CompareChangesStatus } from "src/components/CompareChangesModal/types";
import { useAppContext } from "src/hooks/useAppContext";
import { subscribeToDocumentUpdates } from "src/state/liveUpdateManager";
import { usePlaylistCompareChangesByIdQuery } from "src/types.g";
import { preparePlaylistCompareChanges } from "./preparePlaylistCompareChanges";

export interface PlaylistCompareChangesModalProps {
  playlistId: string;
  onClose: () => void;
  /**
   * Performs the publish and resolves on success / rejects on failure. It must
   * NOT close the modal — the container closes on success and shows the error
   * state on failure (GRWBKK-1680).
   */
  onPublish: () => void | Promise<void>;
}

/**
 * Container for the Compare Changes modal on the Playlist detail page
 * (GRWBKK-1656 / GRWBKK-1680). The app modal manager freezes the element it
 * renders, so this component receives only stable props and owns everything
 * that changes while the modal is open: it queries playlistCompareChangesById,
 * subscribes to live updates for both the playlist and its draft, recomputes
 * the diff on every render, and owns the publish and retry lifecycles.
 *
 * A failed comparison (initial prepare or a realtime-triggered recalculation)
 * and a failed publish share one unified error state; Retry either recomputes
 * the diff or re-attempts the publish, depending on which failed.
 */
export const PlaylistCompareChangesModal = ({
  playlistId,
  onClose,
  onPublish,
}: PlaylistCompareChangesModalProps) => {
  const context = useAppContext();
  const { data, error, loading, refetch } = usePlaylistCompareChangesByIdQuery({
    variables: { id: playlistId },
    fetchPolicy: "cache-and-network",
    // Surface refetch (retry / realtime recalculation) as `loading` so the
    // retry-from-error transition can show the loading state.
    notifyOnNetworkStatusChange: true,
  });

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(false);
  // True only while an explicit Retry is refetching, so retrying from the error
  // state shows loading — a background realtime recalculation must not.
  const [isRetrying, setIsRetrying] = useState(false);

  const playlist = data?.playlistById;
  const draftId = playlist?.draft?.id;
  const orgId = context.user.claims.orgId || "";

  useEffect(() => {
    const unsubscribers = [playlistId, draftId]
      .filter((id): id is string => Boolean(id))
      .map((id) =>
        subscribeToDocumentUpdates(
          orgId,
          EntityType.PLAYLIST,
          id,
          () => {
            // Background recalculation; a failure surfaces via the hook's
            // `error` and flips the modal to the error state.
            Promise.resolve(refetch()).catch(() => undefined);
          },
          true,
        ),
      );
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [orgId, playlistId, draftId, refetch]);

  // Clear the explicit-retry flag once the refetch settles.
  useEffect(() => {
    if (!loading) {
      setIsRetrying(false);
    }
  }, [loading]);

  const handlePublish = useCallback(async () => {
    if (isPublishing) {
      return; // guard against a second submit while one is in flight
    }
    setPublishError(false);
    setIsPublishing(true);
    try {
      await onPublish();
      // Publish succeeded — close the modal. We deliberately do not reset
      // isPublishing afterwards: onClose() unmounts this modal, so a state
      // update here would land on an unmounted component. Closing removes the
      // modal outright, so the button is never left stuck on "Publishing…".
      onClose();
    } catch {
      setPublishError(true);
      setIsPublishing(false);
    }
  }, [isPublishing, onPublish, onClose]);

  const handleRetry = useCallback(() => {
    if (publishError) {
      handlePublish();
      return;
    }
    setIsRetrying(true);
    Promise.resolve(refetch()).catch(() => undefined);
  }, [publishError, handlePublish, refetch]);

  // Compute the diff. If the diff builder throws on unexpected data, degrade to
  // the unified error state (recoverable via Retry) rather than crashing the
  // render — nothing wraps this modal in an ErrorBoundary.
  let compare: ReturnType<typeof preparePlaylistCompareChanges> | undefined;
  let prepareError = false;
  try {
    compare =
      playlist && playlist.draft
        ? preparePlaylistCompareChanges(
            { ...playlist, draft: playlist.draft },
            {
              intl: context.intl,
              secureMediaPolicy: context.secureMediaPolicy,
            },
          )
        : undefined;
  } catch {
    prepareError = true;
  }

  // Mirror the modal's own no-change test: a result only has something to show
  // when it reports changes and has rows for them.
  const hasComparisonChanges =
    (compare?.changes.totalChanges ?? 0) > 0 &&
    (compare?.changes.items.length ?? 0) > 0;

  // The freshest comparison result always wins over what is already on screen:
  // - An explicit Retry, or an initial/blocking fetch with nothing to show yet,
  //   renders the loading skeleton. A cached diff stays visible during a
  //   background revalidation so it does not flash.
  // - Otherwise the latest outcome takes over the stale diff. A failed publish,
  //   a diff-builder throw, or a failed recalculation surfaces the error state
  //   rather than leaving outdated data on screen for the user to act on.
  const status: CompareChangesStatus =
    isRetrying || (loading && !hasComparisonChanges)
      ? "loading"
      : publishError || prepareError || error
        ? "error"
        : "loaded";

  return (
    <CompareChangesModal
      status={status}
      currentlyLive={compare?.currentlyLive}
      changes={compare?.changes}
      isPublishing={isPublishing}
      onClose={onClose}
      onPublish={handlePublish}
      onRetry={handleRetry}
    />
  );
};
